import { ServiceConfig } from '../services/config'
import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  APIError,
  APIReject,
  APIResponse,
  APIValidationError,
  get,
  post,
  put,
  remove,
} from '../services/crud'
import {
  ICrossBranchReassign,
  IExportInput,
  ILead,
  ILeadContactAttach,
  ILeadContactDeleteModel,
  ILeadCompanyRequiredFieldsInput,
  ILeadMissingFieldDTO,
  ILeadContactUpdate as ILeadContactUpdate,
  ILeadContactUpdateResponse as IUpdateLeadSecondaryContactResponse,
  ILeadContactUpdateResponse,
  ILeadDetail,
  ILeadDTO,
  ILeadInput,
  ILeadNextActionDateUpdate,
  ILeadStatusUpdate,
  IPrimaryContactModel,
  LeadInfo,
  LeadName,
} from './typings'
import { StoreData } from '../reducers/typings'
import {
  CustomFieldValueInput,
  ENTITY_TYPES,
  ISalesTeamReassignDTO,
  ISalesTeamReassignUpdate,
  ODataResponse,
} from '@sales-center/common'
import buildQuery from 'odata-query'
import { IContactInput, IPushContactInput } from '../contacts/typings'
import { checkNotSelectedCampaignIdAndSourceId } from '@user/utils/CheckSourceAndCampaign'
import {
  BulkMode,
  BulkUpdateStatusDTO,
  BulkUpdateStatusInput,
} from '@user/components/xGrid'
import {
  BulkReopenDTO,
  BulkReopenInput,
} from '@user/components/xGrid/atoms/BulkActions/typings/BulkReopen'
import {
  BulkReassignDTO,
  BulkReassignInput,
} from '@user/components/xGrid/atoms/BulkActions/typings/BulkReassign'
import {
  ILocationDetailParams,
  IServiceLocationDetails,
} from '../locations/typings'
import {
  IReferralUpdate,
  IReferralUpdatePostModel,
  IServiceTax,
  IServiceTaxUpdate,
} from '../serviceTax/typings'
import { IBulkNextActionDateInput } from '@user/components/xGrid/atoms/BulkActions/typings/BulkUpdateNextActionDate/BulkNextActionDateInput'
import { setEntityLocations } from '../entityLocations'

const baseUrl = `${ServiceConfig.baseAPIUrl}/Lead`
const odataUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.lead.odata}`
const odataExportUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.lead.odataExport}`
const contactBaseUrl = `${ServiceConfig.baseAPIUrl}/Contact`
const secondaryContactBaseUrl = `${ServiceConfig.baseAPIUrl}/LeadSecondaryContact`

const baseExpand = ['assignee', 'billToContact', 'serviceContact', 'services']

export const getLeadList = createAsyncThunk<
  ODataResponse<ILead>,
  boolean | undefined,
  { rejectValue: APIError }
>('lead/cards', async (cardCaptureRefresh = true, thunkApi) => {
  const {
    leadsState: {
      page,
      perPage,
      filterSet: { normalizedFilters, orderBy, search },
      mode,
    },
  } = thunkApi.getState() as StoreData

  const expand =
    mode === 'list'
      ? [...baseExpand, 'createdBy', 'services', 'odataCustomFieldValues']
      : [...baseExpand]

  const qstring = buildQuery<ILead>({
    count: true,
    skip: (page - 1) * perPage,
    top: perPage,
    orderBy,
    expand,
    filter: normalizedFilters,
  })
  const response = await get(
    `${odataUrl}${qstring}&cardCaptureRefresh=${cardCaptureRefresh}&contactSearch=${!!search?.value}`,
  )

  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const getLeadListExport = createAsyncThunk<
  string,
  IExportInput,
  { rejectValue: APIError }
>('lead/cards/export', async (params, thunkApi) => {
  const {
    leadsState: {
      filterSet: { normalizedFilters, orderBy },
      mode,
    },
  } = thunkApi.getState() as StoreData

  const expand =
    mode === 'list' ? ['assignee', 'createdBy', 'services'] : ['assignee']

  const qstring = buildQuery<ILead>({
    skip: 0,
    top: 0,
    orderBy,
    expand,
    filter: normalizedFilters,
  })

  const response = await get(
    `${odataExportUrl}${qstring}&selectedColumns=${params.columns}&showMultipleLocations=${params.multiServiceLocations}`,
  )

  if (response.ok) {
    return await (response as Response).text()
  }
  return thunkApi.rejectWithValue(await response.json())
})

export const hasMultiServiceLocations = createAsyncThunk<
  ODataResponse<ILead>,
  void,
  { rejectValue: APIError }
>('lead/cards/multiServiceLocation', async (columns, thunkApi) => {
  const expand = ['assignee', 'createdBy', 'services']

  const {
    leadsState: {
      filterSet: { normalizedFilters },
    },
  } = thunkApi.getState() as StoreData

  const qstring = buildQuery<ILead>({
    skip: 0,
    top: 1,
    expand,
    filter: [...normalizedFilters, '(ServiceLocationsCount gt 1)'],
  })

  const response = await get(`${odataUrl}${qstring}&selectedColumns=${columns}`)

  if (response.ok) {
    return await response.json()
  }
  return thunkApi.rejectWithValue(await response.json())
})

export const getLeadValue = createAsyncThunk<
  ODataResponse<ILead>,
  number,
  { rejectValue: APIError }
>('lead/value', async (id, thunkApi) => {
  const qstring = buildQuery<ILead>({
    filter: { id },
  })

  const response = await get(`${odataUrl}${qstring}`)

  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

// ========== U P D A T E   L E A D   N E X T   A C T I O N   D A T E ==========
export const updateLeadNextActionDate = createAsyncThunk<
  ILeadNextActionDateUpdate,
  ILeadNextActionDateUpdate,
  { rejectValue: APIError }
>('updateLeadNextActionDate', async (data, thunkApi) => {
  const response = await post(`${baseUrl}/NextActionDate`, data)
  if (response.ok) {
    return data
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

// ========== U P D A T E  R E A S S I G N   L E A D  ==========
export const updateSaleTeamOwner = createAsyncThunk<
  ISalesTeamReassignDTO,
  ISalesTeamReassignUpdate,
  { rejectValue: APIError }
>('updateReassignLead', async (data: ISalesTeamReassignUpdate, thunkApi) => {
  const response = await post(`${baseUrl}/Reassign`, data)

  if (response.ok) {
    const payload: ILeadDTO = await response.json()
    return payload
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

export const updateLeadCompanyRequiredFields = createAsyncThunk<
  ILeadMissingFieldDTO[],
  { leadId: number; input: ILeadCompanyRequiredFieldsInput },
  { rejectValue: APIReject }
>('updateLeadCompanyRequiredFields', async ({ leadId, input }, thunkApi) => {
  const response = await put(
    `${ServiceConfig.baseAPIUrl}${ServiceConfig.lead.companyRequiredFields(leadId)}`,
    input,
  )
  if (response.ok) {
    return (await response.json()) as ILeadMissingFieldDTO[]
  }
  return thunkApi.rejectWithValue((await response.json()) as APIReject)
})

// ========== U P D A T E   L E A D   S T A T U S ==========
export const updateLeadStatus = createAsyncThunk<
  ILeadStatusUpdate,
  ILeadStatusUpdate,
  { rejectValue: APIError }
>('updateLeadStatus', async (data, thunkApi) => {
  const response = await post(`${baseUrl}/UpdateStatus`, data)

  if (response.ok) {
    const payload: ILeadStatusUpdate = await response.json()

    payload.disqualifiedDate = payload.disqualifiedDate
      ? new Date(payload.disqualifiedDate)
      : null

    payload.leadId = data.leadId
    payload.customReason = payload.customReason || data.customReason
    return payload
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

export const createLead = createAsyncThunk<
  ILeadDTO,
  ILeadInput,
  { rejectValue: APIReject }
>('lead/create', async (model, thunkApi) => {
  if (checkNotSelectedCampaignIdAndSourceId(String(model.campaignId))) {
    model.campaignId = null
    model.campaign = null
  }

  if (checkNotSelectedCampaignIdAndSourceId(String(model.leadSourceId))) {
    model.leadSourceId = null
    model.leadSource = null
  }
  const response = await post(`${baseUrl}`, model)
  if (!response.ok) return thunkApi.rejectWithValue(await response.json())
  const payload: ILeadDTO = await response.json()
  return payload
})

export const getLeadDetail = createAsyncThunk<
  ILeadDetail,
  number,
  { rejectValue: APIError }
>('lead/getLeadDetail', async (leadId, thunkApi) => {
  const response = await get(`${baseUrl}/${leadId}`)

  if (response.ok) {
    const payload: ILeadDetail = await response.json()

    thunkApi.dispatch(setEntityLocations(payload.locations))
    const primaryContact = payload.primaryContact
    return {
      ...payload,
      entityType: ENTITY_TYPES.Lead,
      closedDate: payload.closedDate ? new Date(payload.closedDate) : null,
      createdTime: new Date(payload.createdTime),
      nextActionDate: payload.nextActionDate
        ? new Date(payload.nextActionDate)
        : null,
      primaryContact: {
        ...primaryContact,
        firstName: primaryContact.firstName ?? '',
        lastName: primaryContact.lastName ?? '',
      },
      updatedDate: payload.updatedDate ? new Date(payload.updatedDate) : null,
    }
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

export const deleteLead = createAsyncThunk<
  number,
  number,
  { rejectValue: APIError }
>('lead/delete', async (id, thunkApi) => {
  const response = await remove(`${baseUrl}/${id}`)
  if (response.ok) return id
  return thunkApi.rejectWithValue(await response.json())
})

export const updateLeadInfo = createAsyncThunk<
  LeadInfo,
  LeadInfo,
  { rejectValue: APIError }
>('lead/updateLeadInfo', async (model, thunkApi) => {
  const targetPests = model.targetPests.length
    ? '[' + model.targetPests.map((t) => t.id).toString() + ']'
    : '[]'

  if (checkNotSelectedCampaignIdAndSourceId(model.campaignId)) {
    model.campaignId = null
    model.campaign = null
  }

  if (checkNotSelectedCampaignIdAndSourceId(model.leadSourceId)) {
    model.leadSourceId = null
    model.leadSource = null
  }
  const response = await put(`${baseUrl}/updateLeadInfo`, {
    ...model,
    targetPests,
  })

  if (response.ok) {
    const updatedLeadInfo: LeadInfo = await response.json()
    model.draftAgreementsUpdated = updatedLeadInfo.draftAgreementsUpdated
    return model
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

export const updateName = createAsyncThunk<
  LeadName,
  LeadName,
  { rejectValue: APIError }
>('lead/updateName', async (model, thunkApi) => {
  const response = await put(`${baseUrl}/updateName`, model)

  if (response.ok) {
    const updatedLead: LeadName = await response.json()
    model.draftAgreementsUpdated = updatedLead.draftAgreementsUpdated
    return model
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

//========== A D D   C O N T A C T ==========
export const addContact = createAsyncThunk<
  IUpdateLeadSecondaryContactResponse,
  ILeadContactUpdate,
  { rejectValue: APIValidationError }
>('lead/addContact', async (updateModel, thunkApi) => {
  const contact: IContactInput = {
    ...updateModel.contact,
    leadId: updateModel.leadId,
    opportunityId: null,
    isPrimaryContact: updateModel.isPrimaryContact,
  }
  const response = await post(`${contactBaseUrl}`, contact)
  if (response.ok) {
    const responseData = await response.json()
    const responseModel: ILeadContactUpdateResponse = {
      contact: responseData,
      isPrimaryContact: updateModel.isPrimaryContact,
      leadId: updateModel.leadId,
    }
    thunkApi.dispatch(getLeadDetail(updateModel.leadId))
    if (contact.isPrimaryContact) {
      thunkApi.dispatch(getLeadList())
    }
    return responseModel
  }
  return thunkApi.rejectWithValue(await response.json())
})

//========== A T T A C H   C O N T A C T ==========
export const attachContact = createAsyncThunk<
  void,
  ILeadContactAttach,
  { rejectValue: APIValidationError }
>('lead/attachContact', async (model, thunkApi) => {
  const response = await post(`${secondaryContactBaseUrl}`, model)
  if (response.ok) {
    thunkApi.dispatch(getLeadDetail(model.leadId))
    thunkApi.dispatch(getLeadList())
  }
})

//========== R E M O V E   C O N T A C T ==========
export const deleteContact = createAsyncThunk<
  ILeadContactDeleteModel,
  ILeadContactDeleteModel,
  { rejectValue: APIResponse }
>('lead/deleteContact', async (model, thunkApi) => {
  const response = await remove(`${secondaryContactBaseUrl}/${model.contactId}`)
  if (response.ok) {
    thunkApi.dispatch(getLeadDetail(model.leadId))
    thunkApi.dispatch(getLeadList())
    return model
  }
  return thunkApi.rejectWithValue((await response.json()) as APIResponse)
})

//========== U P D A T E    C O N T A C T ==========
export const updateContact = createAsyncThunk<
  IUpdateLeadSecondaryContactResponse,
  ILeadContactUpdate,
  { rejectValue: APIValidationError }
>('lead/updateContact', async (updateModel, thunkApi) => {
  const contact: IContactInput = {
    ...updateModel.contact,
    leadId: updateModel.leadId,
    opportunityId: null,
    isPrimaryContact: updateModel.isPrimaryContact,
  }
  const response = await put(`${contactBaseUrl}`, contact)
  if (response.ok) {
    const responseData = await response.json()
    const responseModel: ILeadContactUpdateResponse = {
      contact: responseData,
      isPrimaryContact: updateModel.isPrimaryContact,
      leadId: updateModel.leadId,
    }
    thunkApi.dispatch(getLeadDetail(updateModel.leadId))
    if (contact.isPrimaryContact) {
      thunkApi.dispatch(getLeadList())
    }
    return responseModel
  }
  return thunkApi.rejectWithValue(await response.json())
})

// =========== S E T   P R I M A R Y   C O N T A C T ===========
export const setPrimaryContact = createAsyncThunk<
  IPrimaryContactModel,
  IPrimaryContactModel,
  { rejectValue: APIResponse }
>('lead/setPrimaryContact', async (model, thunkApi) => {
  const response = await post(`${baseUrl}/setPrimaryContact`, model)
  if (response.ok) {
    thunkApi.dispatch(getLeadDetail(model.id))
    thunkApi.dispatch(getLeadList())
    const updatedPrimaryContact: IPrimaryContactModel = await response.json()
    model.draftAgreementsUpdated = updatedPrimaryContact.draftAgreementsUpdated
    return model
  }
  return thunkApi.rejectWithValue((await response.json()) as APIResponse)
})

export const incrementLeadPaymentsCount = createAsyncThunk<
  number,
  number,
  { rejectValue: APIError }
>('lead/incrementPaymentsCount', async (id, thunkApi) => {
  const response = await post(
    `${baseUrl}/incrementPaymentsCount`,
    id.toString(),
  )

  if (response.ok) {
    return id
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

//========== P U S H   C O N T A C T   T O   L O C A T I O N ==========
export const pushContact = createAsyncThunk<
  number,
  IPushContactInput,
  { rejectValue: APIValidationError }
>('contact/update', async (model, thunkApi) => {
  const response = await post(`${contactBaseUrl}/pushContact`, model)

  if (response.ok) {
    thunkApi.dispatch(getLeadDetail(model.entityId))
    return model.entityId
  }
  return thunkApi.rejectWithValue(await response.json())
})

//#region bulk actions
export const bulkDeleteLeads = createAsyncThunk<
  void,
  number[],
  { rejectValue: APIError }
>('lead/bulk/delete', async (ids, thunkApi) => {
  const response = await remove(`${baseUrl}/bulkDelete`, ids)
  if (!response.ok) return thunkApi.rejectWithValue(await response.json())
})

export const bulkUpdateLeadStatus = createAsyncThunk<
  { mode: BulkMode; model: BulkUpdateStatusDTO },
  { mode?: BulkMode; model: BulkUpdateStatusInput },
  { rejectValue: APIError }
>('lead/bulk/status', async ({ mode = 'validate', model }, thunkApi) => {
  const response = await post(`${baseUrl}/bulkUpdateStatus?mode=${mode}`, model)
  if (response.ok) return { mode, model: await response.json() }
  return thunkApi.rejectWithValue(await response.json())
})

export const bulkReopenLeads = createAsyncThunk<
  { mode: BulkMode; model: BulkReopenDTO },
  { mode?: BulkMode; model: BulkReopenInput },
  { rejectValue: APIError }
>('lead/bulk/reopen', async ({ mode = 'validate', model }, thunkApi) => {
  const response = await post(`${baseUrl}/bulkReopen?mode=${mode}`, model)
  if (response.ok) return { mode, model: await response.json() }
  return thunkApi.rejectWithValue(await response.json())
})

export const bulkReassignLeads = createAsyncThunk<
  { mode: BulkMode; model: BulkReassignDTO },
  { mode?: BulkMode; model: BulkReassignInput },
  { rejectValue: APIError }
>('lead/bulk/reassign', async ({ mode = 'validate', model }, thunkApi) => {
  const response = await post(`${baseUrl}/bulkReassign?mode=${mode}`, model)
  if (response.ok) return { mode, model: await response.json() }
  return thunkApi.rejectWithValue(await response.json())
})

export const bulkUpdateNextActionDateLeads = createAsyncThunk<
  void,
  IBulkNextActionDateInput,
  { rejectValue: APIError }
>('lead/bulk/UpdateNextActionDate', async (input, thunkApi) => {
  const response = await post(`${baseUrl}/bulkUpdateNextActionDate`, input)
  if (!response.ok) return thunkApi.rejectWithValue(await response.json())
})

export const getLocationDetailsForLead = createAsyncThunk<
  IServiceLocationDetails,
  ILocationDetailParams
>('lead/locationDetailCard', async (model, thunkApi) => {
  const response = await get(
    `${baseUrl}/GetLocationDetails/${model.entityId}?locationId=${model.locationId}`,
  )
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})
//#endregion

// ========== U P D A T E   T A X   C O D E ==========
export const updateLeadTaxCode = createAsyncThunk<
  IServiceTax,
  IServiceTaxUpdate,
  { rejectValue: APIError }
>('updateLeadTaxCode', async (data, thunkApi) => {
  const response = await put(`${baseUrl}/updateTaxRate`, data)
  if (response.ok) {
    const payload: IServiceTax = await response.json()
    return payload
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

// ========== U P D A T E   R E F E R R A L ==========
export const updateReferral = createAsyncThunk<
  IReferralUpdate,
  IReferralUpdate,
  { rejectValue: APIError }
>('lead/updateReferral', async (model, thunkApi) => {
  const postModel: IReferralUpdatePostModel = {
    id: model.entityId,
    ...model.referral,
    referralType: model.referral ? model.referral.referralType : null,
  }

  const response = await put(`${baseUrl}/updateReferral`, postModel)
  if (response.ok) {
    await thunkApi.dispatch(getLeadDetail(model.entityId))
    return model
  }
  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

export const getCrossBranchReassign = createAsyncThunk<
  ICrossBranchReassign,
  number
>('salesTeams/getCrossBranchReassign', async (id, thunkApi) => {
  const response = await get(`${baseUrl}/${id}/ValidateCrossBranchReassign`)
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue(await response.json())
})

export const updateMissingAutoCloseRequiredFields = createAsyncThunk<
  void,
  { leadId: number; values: CustomFieldValueInput[] },
  { rejectValue: APIError }
>(
  'lead/updateMissingAutoCloseRequiredFields',
  async ({ leadId, values }, thunkApi) => {
    const response = await put(
      `${baseUrl}/${leadId}${ServiceConfig.lead.missingAutoCloseRequiredFields}`,
      values,
    )
    if (!response.ok) {
      return thunkApi.rejectWithValue((await response.json()) as APIError)
    }
  },
)
